import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Lock as LockIcon } from '@mui/icons-material';
import { useAdmin } from '../contexts/AdminContext';

interface AdminPinModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AdminPinModal: React.FC<AdminPinModalProps> = ({ open, onClose, onSuccess }) => {
  const { login } = useAdmin();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!pin.trim()) return;

    setLoading(true);
    setError('');

    const result = await login(pin);

    if (result.success) {
      setPin('');
      onClose();
      onSuccess?.();
    } else {
      setError(result.error || 'Invalid PIN');
    }

    setLoading(false);
  };

  const handleClose = () => {
    setPin('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #FFFAF5 0%, #FFF5EB 100%)',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>Admin Access</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the admin PIN to access the dashboard.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Admin PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            inputProps={{ maxLength: 12, inputMode: 'numeric' }}
            disabled={loading}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!pin.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <LockIcon />}
          >
            {loading ? 'Verifying...' : 'Unlock'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AdminPinModal;
